import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GamePerformanceTable = () => {
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');

  const games = [
    {
      id: 1,
      name: 'Space Quest',
      plays: 12540,
      revenue: 4200.50,
      revenuePerPlay: 0.335,
      rating: 4.7,
      trend: 'up',
      trendValue: '+15.2%'
    },
    {
      id: 2,
      name: 'Pixel Runner',
      plays: 9821,
      revenue: 3800.25,
      revenuePerPlay: 0.387,
      rating: 4.5,
      trend: 'up',
      trendValue: '+8.9%'
    },
    {
      id: 3,
      name: 'Mystic Realms',
      plays: 7650,
      revenue: 2600.75,
      revenuePerPlay: 0.340,
      rating: 4.3,
      trend: 'down',
      trendValue: '-3.4%'
    },
    {
      id: 4,
      name: 'Cyber Strike',
      plays: 5432,
      revenue: 2100.00,
      revenuePerPlay: 0.387,
      rating: 4.8,
      trend: 'up',
      trendValue: '+22.1%'
    },
    {
      id: 5,
      name: 'Adventure Island',
      plays: 4321,
      revenue: 1800.60,
      revenuePerPlay: 0.417,
      rating: 4.2,
      trend: 'stable',
      trendValue: '+1.2%'
    }
  ];

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedGames = [...games].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getTrendIcon = (trend) => {
    if (trend === 'up') return 'TrendingUp';
    if (trend === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <h2 className="text-xl font-semibold text-foreground">Game Performance Breakdown</h2>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Icon name="Search" size={16} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search games..."
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm">
            <Icon name="Filter" size={16} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Game</th>
              <th 
                className="text-left py-3 px-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('plays')}
              >
                <div className="flex items-center space-x-1">
                  <span>Plays</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left py-3 px-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('revenue')}
              >
                <div className="flex items-center space-x-1">
                  <span>Revenue</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left py-3 px-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('revenuePerPlay')}
              >
                <div className="flex items-center space-x-1">
                  <span>Revenue/Play</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left py-3 px-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => handleSort('rating')}
              >
                <div className="flex items-center space-x-1">
                  <span>Rating</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">Trend</th>
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game) => (
              <tr key={game.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{game.name}</div>
                </td>
                <td className="py-3 px-4 text-foreground">{game.plays.toLocaleString()}</td>
                <td className="py-3 px-4 text-foreground">${game.revenue.toFixed(2)}</td>
                <td className="py-3 px-4 text-foreground">${game.revenuePerPlay.toFixed(3)}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} className="text-yellow-500" />
                    <span className="text-foreground">{game.rating}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className={`flex items-center space-x-1 ${getTrendColor(game.trend)}`}>
                    <Icon name={getTrendIcon(game.trend)} size={14} />
                    <span className="text-sm font-medium">{game.trendValue}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GamePerformanceTable;
